import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  Box, Text, Layer, Button,
} from 'grommet';
import { FormClose } from 'grommet-icons';

import { AppButton } from '@Components/Control';


const StyledCloseContainer = styled(Box)`
  svg { padding-top: 0.1rem };

  &:hover {
    span {
      color: ${({ highlight }) => highlight};
    }

    svg {
      stroke: ${({ highlight }) => highlight};
      fill: ${({ highlight }) => highlight};
    }
  }
`;

const StyledRaisedContainer = styled(Box)`
  border-radius: var(--app-radius);
  box-shadow: var(--elevation);
  background-color: ${({ bgColor }) => bgColor};
`;

const SimpleModal = ({
  small,
  toggleModal,
  modalTitle,
  textColor = 'var(--page-modal-text)',
  highlightColor = 'var(--page-highlight)',
  children,
  closeLabel = 'Finished',
  modalBg = 'var(--page-modal-bg)',
  customHeaderBg = null,
  hideLowerSection = false,
  desktopMinWidth = '50rem',
  noClickOutside = false,
  titleSize = '1rem',
  customClickOutside,
}) => {
  const handleClickOutside = () => {
    if (customClickOutside) {
      customClickOutside();
      toggleModal(false);
    } else if (!noClickOutside) {
      toggleModal(false);
    }
  };

  return (
    <Layer
      responsive={false}
      animate={false}
      onEsc={() => toggleModal(false)}
      onClickOutside={handleClickOutside}
    >
      <StyledRaisedContainer
        gap="0.5rem"
        align="center"
        width={{ min: small ? '96vw' : desktopMinWidth }}
        bgColor={modalBg}
      >
        <Box
          direction="row"
          width="100%"
          justify="between"
          pad={{ top: '1.5rem', bottom: '1rem', horizontal: '1.5rem' }}
          border={{
            color: 'var(--nav-border)',
            size: '1px',
            style: 'solid',
            side: 'bottom',
          }}
          round={{ size: '0.25rem', corner: 'top' }}
          background={customHeaderBg || modalBg}
        >
          <Text weight={600} size={titleSize} color={textColor}>
            {modalTitle}
          </Text>
          <Button
            plain
            onClick={() => toggleModal(false)}
          >
            <StyledCloseContainer highlight={highlightColor} direction="row" gap="0.25rem" align="center">
              <Text weight={600} size="1.1rem" color={textColor}>
                Close
              </Text>
              <FormClose color={textColor} size="1.3rem" />
            </StyledCloseContainer>
          </Button>
        </Box>
        <Box width="100%" pad={{ bottom: hideLowerSection ? '1.5rem' : '0', horizontal: '1.5rem' }}>
          {children}
        </Box>
        {!hideLowerSection && (
          <Box
            width="100%"
            pad={{ top: '1rem', bottom: '1.5rem' }}
            align="center"
          >
            <AppButton
              overrideHover
              onClick={() => toggleModal(false)}
              level="dynamicSmall"
              color="var(--button-primary-text)"
              bgColor={highlightColor || 'var(--button-primary-highlight)'}
              fontWeight={600}
              label={closeLabel}
            />
          </Box>
        )}
      </StyledRaisedContainer>
    </Layer>
  );
};

SimpleModal.propTypes = {
  small: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  modalTitle: PropTypes.string.isRequired,
  textColor: PropTypes.string,
  highlightColor: PropTypes.string,
  children: PropTypes.node.isRequired,
  modalBg: PropTypes.string,
  customHeaderBg: PropTypes.string,
  closeLabel: PropTypes.string,
  hideLowerSection: PropTypes.bool,
  desktopMinWidth: PropTypes.string,
  noClickOutside: PropTypes.bool,
  titleSize: PropTypes.string,
  customClickOutside: PropTypes.func,
};

export default SimpleModal;
